/**
 * Query-key conventions for the web app.
 *
 * Keys are hierarchical arrays: ['domain', scopeId?, 'sub-resource'?, params?].
 * Always build keys through these factories — string-literal keys scattered
 * through components is how cache invalidation breaks silently.
 *
 * Invalidation examples:
 *   queryClient.invalidateQueries({ queryKey: queryKeys.locations.all })
 *   queryClient.invalidateQueries({ queryKey: queryKeys.users.byAccount(accountId) })
 */
export const queryKeys = {
  /** The signed-in person's own permissions at one location (what the API checks edits there against). */
  myPermissionsAt: (userId: string, locationId: string) => ['auth', 'me-permissions', userId, locationId] as const,
  /** The locations the signed-in person can open in one account (the settings location picker). */
  myLocationsInAccount: (userId: string, accountId: string) => ['auth', 'my-locations', userId, accountId] as const,
  myLocationsSearch: (userId: string, accountId: string, query: string) =>
    ['auth', 'my-locations', userId, accountId, 'search', query] as const,
  /** Prefix of every my-locations list, refreshed after a location is created, renamed or deleted. */
  myLocationsAll: ['auth', 'my-locations'] as const,
  /** Every account the signed-in person can work in, with its locations (GET /auth/contexts). */
  workContexts: (userId: string) => ['auth', 'contexts', userId] as const,
  accounts: {
    all: ['accounts'] as const,
    list: (includeDeleted: boolean) => ['accounts', 'list', { includeDeleted }] as const,
    /** Account ids and names (/accounts/summaries) — the light list for selectors. */
    summaries: ['accounts', 'summaries'] as const,
    /** Accounts the current user is a member of (/auth/accounts) — session-scoped. */
    mine: ['accounts', 'mine'] as const,
    /** One account's full row (GET /accounts/:id) — settings flags such as allow_service_splits. */
    detail: (accountId: string) => ['accounts', 'detail', accountId] as const,
    /** Every account's detail entry — for writes, which refresh whichever accounts are cached. */
    detailAny: ['accounts', 'detail'] as const,
    /** Account display names resolved individually for users whose accounts are derived from location roles. */
    names: (accountIds: string[]) => ['accounts', 'names', accountIds] as const,
  },
  locations: {
    all: ['locations'] as const,
    /**
     * App-level location SELECTOR list — caches the shaped
     * `{ options, firstIdInApiOrder }` object (see appData.ts), NOT a raw
     * Location[]. Keep this distinct from `detailByAccount`: the two return
     * different shapes, so sharing a key let the selector's object poison the
     * modal's array-expecting query (empty LocationAccessGrid for account
     * admins — no refetch fired because the cache was "fresh").
     */
    byAccount: (accountId: string) => ['locations', 'byAccount', accountId] as const,
    /** Raw Location[] for an account — used by the user/invite modals' access grids. */
    detailByAccount: (accountId: string) => ['locations', 'detailByAccount', accountId] as const,
    /** Every account's detailByAccount entries (names, pages) — for writes that know only a location id. */
    detailByAnyAccount: ['locations', 'detailByAccount'] as const,
    /** Locations the current user can access (/auth/my-locations) — session-scoped. */
    mine: ['locations', 'mine'] as const,
    dashboard: (locationId: string, params?: Record<string, unknown>) => ['locations', 'dashboard', locationId, params ?? {}] as const,
    /** Prefix key matching every tag/user variant of a location's dashboard — for invalidation. */
    dashboardAll: (locationId: string) => ['locations', 'dashboard', locationId] as const,
  },
  departments: {
    byLocation: (locationId: string) => ['departments', 'byLocation', locationId] as const,
  },
  tags: {
    /** Every tag cache (per location, summaries, names) — for tag writes. */
    all: ['tags'] as const,
    byLocation: (locationId: string) => ['tags', 'byLocation', locationId] as const,
    /**
     * Tag-name summary (GET /accounts/:id/tags/summary) — TagSummaryItem[].
     * Keyed by account AND the sorted location scope ([] = whole account).
     */
    summaryByAccount: (accountId: string, sortedLocationIds: string[]) =>
      ['tags', 'summaryByAccount', accountId, sortedLocationIds] as const,
    /** Every tag NAME across an account (string[]) — the rule editors' TagPicker suggestions. */
    namesByAccount: (accountId: string) => ['tags', 'namesByAccount', accountId] as const,
  },
  users: {
    byAccount: (accountId: string, params?: Record<string, unknown>) => ['users', 'byAccount', accountId, params ?? {}] as const,
    /** Prefix for everything cached about one user — invalidate this after a user save. */
    byId: (userId: string) => ['users', userId] as const,
    locations: (userId: string) => ['users', userId, 'locations'] as const,
    payroll: (userId: string) => ['users', userId, 'payroll'] as const,
    permissionOverrides: (userId: string) => ['users', userId, 'permissionOverrides'] as const,
    communications: (userId: string) => ['users', userId, 'communications'] as const,
    emailDelivery: (userId: string, accountId: string, offset: number) => ['users', userId, 'emailDelivery', accountId, offset] as const,
  },
  roles: {
    byAccount: (accountId: string, params?: Record<string, unknown>) => ['roles', 'byAccount', accountId, params ?? {}] as const,
  },
  permissions: {
    catalog: ['permissions', 'catalog'] as const,
  },
  settings: {
    /** Prefix for everything about an account's default settings — invalidate after a bulk reset/set. */
    byAccount: (accountId: string) => ['settings', accountId] as const,
    /** The families/fields catalogue (labels + kinds). */
    families: (accountId: string) => ['settings', accountId, 'families'] as const,
    /** Locations that differ from the account; keyed on the wire filter so views share one fetch. */
    deviations: (accountId: string, params?: Record<string, unknown>) => ['settings', accountId, 'deviations', params ?? {}] as const,
    /** How many locations customize anything (the Location customizations badge): one row fetched, not the report. */
    customizedLocationCount: (accountId: string) => ['settings', accountId, 'customized-location-count'] as const,
    /** Account-wide geofence centre summary; under the settings prefix so a location save refreshes it too. */
    geofenceHealth: (accountId: string) => ['settings', accountId, 'geofence-health'] as const,
    /** One location's "N custom" badge counts; under the settings prefix so bulk resets refresh it. */
    customCounts: (accountId: string, locationId: string) => ['settings', accountId, 'custom-counts', locationId] as const,
  },
  settingsOverview: {
    /** Every settings Overview (account and location) — invalidate after a settings save. */
    all: ['settings-overview'] as const,
    account: (accountId: string) => ['settings-overview', 'account', accountId] as const,
    location: (locationId: string) => ['settings-overview', 'location', locationId] as const,
  },
  pdr: {
    /** A work order's PDR estimate, as the work-order card reads it. */
    estimate: (workOrderId: string) => ['pdr', 'estimate', workOrderId] as const,
  },
  locationGroups: {
    /** Prefix for everything about an account's location groups — invalidate after any group write. */
    byAccount: (accountId: string) => ['location-groups', accountId] as const,
    list: (accountId: string) => ['location-groups', accountId, 'list'] as const,
    members: (accountId: string, groupId: string) => ['location-groups', accountId, 'members', groupId] as const,
    /** A user's group role follows in the account. */
    userFollows: (accountId: string, userId: string) => ['location-groups', accountId, 'user-follows', userId] as const,
  },
  invoicing: {
    /** Prefix for every param variant of a location's queue — invalidate this after send. */
    queueAll: (locationId: string) => ['invoicing', 'queue', locationId] as const,
    queue: (locationId: string, params?: Record<string, unknown>) => ['invoicing', 'queue', locationId, params ?? {}] as const,
    batches: (params: Record<string, unknown>) => ['invoicing', 'batches', params] as const,
    /** Prefix for a location's invoices made today — invalidate after a send (not on every live nudge). */
    todayAll: (locationId: string) => ['invoicing', 'invoiced-today', locationId] as const,
    /** A location's invoices made today, per day, zone and tag. */
    today: (locationId: string, params: Record<string, unknown>) => ['invoicing', 'invoiced-today', locationId, params] as const,
  },
  reports: {
    all: ['reports'] as const,
    /** Prefix for every param variant of one report type — invalidate after a mutation that changes its rows. */
    type: (reportType: string) => ['reports', reportType] as const,
    /** One report payload, keyed on the full wire-param object (scope, dates, filters, sort, offset). */
    data: (reportType: string, params: Record<string, unknown>) => ['reports', reportType, params] as const,
    /** Dept/user filter dropdown options (account-level fetch; per-selection lists derived client-side). */
    filterOptions: (accountId: string, params: Record<string, unknown>) => ['reports', 'filterOptions', accountId, params] as const,
    /** Server-side employee search for the filter dropdown (accounts past the roster cap). */
    userSearch: (accountId: string, search: string) => ['reports', 'userSearch', accountId, search] as const,
    /** Accounts selectable in the billing report filter. */
    billingAccounts: (isSystemAdmin: boolean) => ['reports', 'billingAccounts', { isSystemAdmin }] as const,
  },
  approvals: {
    /** Prefix for every param variant of a location's queue — invalidate this after approve/deny/send. */
    queueAll: (locationId: string) => ['approvals', 'queue', locationId] as const,
    queue: (locationId: string, params?: Record<string, unknown>) => ['approvals', 'queue', locationId, params ?? {}] as const,
  },
} as const;
